import { ChangeDetectionStrategy, Component, EventEmitter, inject, Input, Output } from '@angular/core';

import { combineLatest, map, Observable, of } from 'rxjs';

import { ApiQuery } from '@stylehaven/components';

import { ColumnManagementService } from '../../../column-management/column-management.service';
import {
    ColumnPickerGroup,
    ColumnSelectionChange,
    ShowSubfoldersChange
} from '../../../column-management/column-picker-group.entity';
import { RecordSearch } from '../../entities/record-search.entity';

/**
 * The row above an overview's table: the search bar, and — for every overview that column management knows
 * about — the Kolombeheer trigger beside it. It owns the picker's staging, so a host only names the ids.
 *
 * An empty {@link overviewIds} renders the search bar alone, which is how a page with no managed columns
 * keeps the row it has always had.
 */
@Component({
    standalone: false,
    selector: 'digihaven-overview-search-row',
    templateUrl: './overview-search-row.component.html',
    styleUrls: ['./overview-search-row.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class OverviewSearchRowComponent {
    private readonly columnManagementService: ColumnManagementService = inject(ColumnManagementService);

    /** The overviews whose columns this row manages — one picker group each, in this order. */
    @Input() set overviewIds(overviewIds: string[]) {
        this.managedOverviewIds = overviewIds ?? [];
        this.columnPickerGroups$ = this.managedOverviewIds.length
            ? combineLatest(
                  this.managedOverviewIds.map(id => this.columnManagementService.getPickerGroup$(id))
              ).pipe(map(groups => groups.filter((group): group is ColumnPickerGroup => group !== null)))
            : of([]);
    }

    @Input() advancedSearchTag: string | undefined;
    @Input() enableAdvancedSearch = false;
    @Input() recordSearch: RecordSearch | null = null;

    @Output() queryChanges: EventEmitter<ApiQuery> = new EventEmitter<ApiQuery>();

    columnPickerGroups$: Observable<ColumnPickerGroup[]> = of([]);

    private managedOverviewIds: string[] = [];

    onQueryChange(query: ApiQuery): void {
        this.queryChanges.emit(query);
    }

    onColumnSelectionChanged(change: ColumnSelectionChange): void {
        this.columnManagementService.stageColumns(change.groupId, change.keys);
    }

    onShowSubfoldersChanged(change: ShowSubfoldersChange): void {
        this.columnManagementService.stageShowSubfolders(change.groupId, change.showSubfolders);
    }

    /** Save, Reset and close are panel-wide, but each level persists on its own key. */
    onColumnsApply(): void {
        this.managedOverviewIds.forEach(overviewId => this.columnManagementService.applyColumns(overviewId));
    }

    onColumnsReset(): void {
        this.managedOverviewIds.forEach(overviewId => this.columnManagementService.resetColumns(overviewId));
    }

    onColumnPickerClosed(): void {
        this.managedOverviewIds.forEach(overviewId => this.columnManagementService.discardPendingChanges(overviewId));
    }
}
